import { useMemo, useState } from "react";
import type { OnStateChange } from "@/components/mosaic/ui/TileTitle/TileTitle";
import { Button } from "@/components/ui/button";
import { Screener } from "@/components/widgets/Screener";
import { FilterDataProvider } from "@/context/FilterDataContext";
import { WidgetViewProvider } from "@/context/WidgetViewContext";
import { cn } from "@/lib/utils";
import { EventEmitter } from "@/shared/eventEmiters/EventEmitter";
import sprites from "/src/shared/assets/sprite.svg";
import { earningsCalendarState } from "../../../config";
import { generateWeekdayFilters } from "../../../lib/helpers/generateWeekdayFilters";
import { getDateFieldForEarningsCalendar } from "../../../lib/helpers/getDateFieldForEarningsCalendar";
import { isToday } from "../../../lib/helpers/isToday";

export const EarningsCalendar = ({
	onStateChange,
}: {
	onStateChange?: OnStateChange;
}) => {
	const [weekOffset, setWeekOffset] = useState(0);

	const weekdays = useMemo(
		() => generateWeekdayFilters(weekOffset),
		[weekOffset],
	);

	const handlePreviousWeekClick = () => setWeekOffset((prev) => prev - 1);
	const handleNextWeekClick = () => setWeekOffset((prev) => prev + 1);

	return (
		<div className="w-full">
			<div className="flex gap-2 justify-between mb-2">
				<Button
					onClick={handlePreviousWeekClick}
					variant="dropdown"
					className="h-[24px] px-1 font-bold"
				>
					<svg
						aria-label="arrow-left"
						role="img"
						fill="currentColor"
						className="w-2 h-2 mr-2"
					>
						<use xlinkHref={`${sprites}#arrow-left`} />
					</svg>
					Previous Week
				</Button>
				<Button
					onClick={handleNextWeekClick}
					variant="dropdown"
					className="h-[24px] px-1 font-bold"
				>
					Next Week
					<svg
						aria-label="arrow-right"
						role="img"
						fill="currentColor"
						className="w-2 h-2 ml-2 rotate-180"
					>
						<use xlinkHref={`${sprites}#arrow-left`} />
					</svg>
				</Button>
			</div>

			<div className="grid w-full gap-3 grid-cols-[repeat(auto-fit,minmax(220px,1fr))]">
				{weekdays.map(({ label, start, end }) => (
					<div key={start} className="w-full">
						<h3
							className={cn(
								"text-base text-light-gray mb-2",
								isToday(new Date(start)) && "text-gold",
							)}
						>
							{label}
						</h3>
						<div className="w-full h-[370px]" data-earnings-calendar>
							<WidgetViewProvider>
								<FilterDataProvider>
									<Screener
										state={{
											...earningsCalendarState,
											filter: [
												...(earningsCalendarState?.filter ?? []),
												{
													name: getDateFieldForEarningsCalendar(
														new Date(start),
													),
													condition: "gte",
													value: start,
												},
												{
													name: getDateFieldForEarningsCalendar(
														new Date(start),
													),
													condition: "lt",
													value: end,
												},
											],
										}}
										eventEmitter={new EventEmitter()}
										tileId={`Earnings-calendar-${label}`}
										onStateChange={(state) => {
											if (state?.ticker) {
												onStateChange?.({ ticker: state.ticker });
											}
										}}
									/>
								</FilterDataProvider>
							</WidgetViewProvider>
						</div>
					</div>
				))}
			</div>
		</div>
	);
};
